import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as React from 'react';

import { CatalogAdminEditionClientProvider } from '@/api/catalog-client-context';
import { HttpCatalogAdminEditionClient } from '@/api/catalog-edition-client.http';
import { toStringList } from '@/lib/search-list';
import {
  CampaignAnalysisScreen,
  type AnalysisSearchPatch,
} from '@/routes/admin/campaigns/campaign-analysis-screen';
import type { RankCode } from '@/api/rank-admin-client';
import type { RankingSortColumn, SortDirection } from '@/lib/analysis-fusion';

// =============================================================================
// campaign-analysis-route.tsx — Adaptateur routeur de la page Analyse FUSIONNÉE (lot U1).
// =============================================================================
// L'id de campagne est le path param d'URL (« /admin/campaigns/$campaignId/analysis ») — ressource
// d'URL, cohérente avec le contrat API (campaignId server-set, invariant n°1). TOUT l'état de vue
// (profil, sous-famille, fournisseur, TOP N, chip de tag, colonne et sens de tri) vit dans les SEARCH
// PARAMS (RULE_REF_006 : aucun localStorage pour l'état métier/vue) : une vue filtrée est PARTAGEABLE et
// REJOUABLE à l'identique (ST-RANK-01 résultat 6). Chaque changement PRÉSERVE les autres paramètres.
//
// Le composant de présentation reste testable hors routeur.
//
// LIBELLÉ DE PROFIL : la page joint FRONT-SIDE le catalogue FIGÉ de l'analyse via `useCatalogProfiles`,
// qui lit le contexte CatalogAdminEditionClient — dont le DÉFAUT global est un STUB (stub-first S8/S9).
// On enveloppe donc CETTE route avec le client HTTP RÉEL (lecture seule), exactement comme la page
// Profils de la vue opérationnelle (C1-bis) : sans lui, le libellé lirait le stub.

/** Client HTTP réel (lecture) du catalogue — injecté pour la jointure « libellé de profil ». */
const catalogEditionClient = new HttpCatalogAdminEditionClient();

const routeApi = getRouteApi('/admin/campaigns/$campaignId/analysis');

/**
 * Forme des search params préservés/mutés à la navigation (état de vue d'URL).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21) : `profil`, `sousFamille` et `fournisseur` sont des LISTES. Leur
 * encodage sur le fil est celui DÉJÀ en vigueur pour `state`/`tokenStatus` du registre fournisseurs,
 * c'est-à-dire celui du sérialiseur de TanStack Router. Une liste VIDE fait disparaître la clé de
 * l'URL — paramètre absent = tous, jamais de valeur sentinelle.
 */
export interface AnalysisSearch {
  profil?: readonly string[];
  sousFamille?: readonly string[];
  fournisseur?: readonly string[];
  topN?: number;
  tag?: RankCode;
  tri?: RankingSortColumn;
  dir?: SortDirection;
}

export function CampaignAnalysisRoute(): React.JSX.Element {
  const { campaignId } = routeApi.useParams();
  const { profil, sousFamille, fournisseur, topN, tag, tri, dir } = routeApi.useSearch();
  const navigate = useNavigate();

  /** Navigue en MUTANT une partie des search params, en préservant le reste (merge sur `prev`). */
  const onChangeSearch = React.useCallback(
    (patch: AnalysisSearchPatch): void => {
      void navigate({
        to: '/admin/campaigns/$campaignId/analysis',
        params: { campaignId },
        // `prev` est l'UNION GLOBALE des search params du routeur (des clés homonymes vivent sur
        // d'autres routes — ex. `tri` du registre clients, typé sur une autre échelle) : on la lit en
        // enregistrement opaque, on fusionne, puis on renvoie la forme de CETTE route.
        search: (prev: Record<string, unknown>): AnalysisSearch => {
          const next: Record<string, unknown> = { ...prev };
          for (const [key, value] of Object.entries(patch)) {
            // Nettoyage : une clé passée à `undefined` — ou à une LISTE VIDE, ce qu'émet un filtre
            // multi-valeurs qu'on vient d'effacer — DISPARAÎT de l'URL (pas de « ?profil= » vide,
            // aucune valeur sentinelle « tous »).
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              delete next[key];
            } else {
              next[key] = value;
            }
          }
          return next as AnalysisSearch;
        },
      });
    },
    [navigate, campaignId],
  );

  /** « Réinitialiser » : retour à l'état initial (aucun filtre, TOP N par défaut, tri par défaut). */
  const onResetSearch = React.useCallback((): void => {
    void navigate({
      to: '/admin/campaigns/$campaignId/analysis',
      params: { campaignId },
      search: (): AnalysisSearch => ({}),
    });
  }, [navigate, campaignId]);

  return (
    <CatalogAdminEditionClientProvider client={catalogEditionClient}>
      <CampaignAnalysisScreen
        campaignId={campaignId}
        // Garde-fou RUNTIME (cf. `lib/search-list.ts`) : une clé écartée par `validateSearch` n'est
        // PAS retirée de la search fusionnée — sa valeur brute (nombre, objet…) atteindrait sinon
        // l'écran et casserait le premier `.map`. Ce qui n'est pas une liste de chaînes vaut « tous ».
        profileCodes={toStringList(profil)}
        sousFamilles={toStringList(sousFamille)}
        supplierSirets={toStringList(fournisseur)}
        topN={topN}
        tag={tag}
        sortColumn={tri}
        sortDirection={dir}
        onChangeSearch={onChangeSearch}
        onResetSearch={onResetSearch}
      />
    </CatalogAdminEditionClientProvider>
  );
}
